import { i18n } from '../../../i18n/index.js';
// Pure markdown formatting helpers for the task description editor. Inline
// kinds (bold, italic, strike, code) wrap the selection; line kinds (heading,
// bullet, numbered, quote) toggle a prefix on the line that contains the
// caret. Every action returns the new text plus the selection to restore.
export type MarkdownFormatKind =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'code'
  | 'heading'
  | 'bullet'
  | 'numbered'
  | 'quote';

export interface MarkdownFormatResult {
  text: string;
  selectionStart: number;
  selectionEnd: number;
}

interface InlineFormat {
  prefix: string;
  suffix: string;
  placeholder: string;
}

const INLINE_FORMATS: Record<'bold' | 'italic' | 'strike' | 'code', InlineFormat> = {
  bold: {
    prefix: '**',
    suffix: '**',
    get placeholder() {
      return i18n.t('crm.features.map.features.tasks.lib.markdownformat.95');
    },
  },
  italic: {
    prefix: '*',
    suffix: '*',
    get placeholder() {
      return i18n.t('crm.features.map.features.tasks.lib.markdownformat.96');
    },
  },
  strike: {
    prefix: '~~',
    suffix: '~~',
    get placeholder() {
      return i18n.t('crm.features.map.features.tasks.lib.markdownformat.97');
    },
  },
  code: {
    prefix: '`',
    suffix: '`',
    get placeholder() {
      return i18n.t('crm.features.map.features.tasks.lib.markdownformat.98');
    },
  },
};

const LINE_PREFIXES: Record<'heading' | 'bullet' | 'numbered' | 'quote', string> = {
  heading: '## ',
  bullet: '- ',
  numbered: '1. ',
  quote: '> ',
};

export function applyMarkdownFormat(
  text: string,
  start: number,
  end: number,
  kind: MarkdownFormatKind,
): MarkdownFormatResult {
  const inline = INLINE_FORMATS[kind as keyof typeof INLINE_FORMATS];
  if (inline) {
    const selected = text.slice(start, end);
    if (selected.length > 0) {
      // Wrap the selection; keep it selected so the next press re-wraps.
      const next = `${text.slice(0, start)}${inline.prefix}${selected}${inline.suffix}${text.slice(end)}`;
      return {
        text: next,
        selectionStart: start + inline.prefix.length,
        selectionEnd: start + inline.prefix.length + selected.length,
      };
    }
    // No selection: insert a template with the placeholder word selected so
    // typing replaces it right away.
    const next = `${text.slice(0, start)}${inline.prefix}${inline.placeholder}${inline.suffix}${text.slice(end)}`;
    return {
      text: next,
      selectionStart: start + inline.prefix.length,
      selectionEnd: start + inline.prefix.length + inline.placeholder.length,
    };
  }

  // Line kinds: toggle the prefix on the line that contains the caret.
  const prefix = LINE_PREFIXES[kind as keyof typeof LINE_PREFIXES];
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  const startsWithPrefix = text.slice(lineStart, lineStart + prefix.length) === prefix;
  if (startsWithPrefix) {
    const next = `${text.slice(0, lineStart)}${text.slice(lineStart + prefix.length)}`;
    return { text: next, selectionStart: lineStart, selectionEnd: lineStart };
  }
  const next = `${text.slice(0, lineStart)}${prefix}${text.slice(lineStart)}`;
  const caret = lineStart + prefix.length;
  return { text: next, selectionStart: caret, selectionEnd: caret };
}
